/**
 * Plain-language classification of a delivery outcome (D-38).
 *
 * Provider adapters return technical error codes such as `fcm_unregistered`. Support staff need to know
 * what happened and what to do next, so every outcome maps to one issue kind. The dashboard translates
 * each kind into an explanation and a suggested action. Unknown codes fall back to `other`.
 */
import type { DeliveryStatus } from "./constants";

export const DELIVERY_ISSUES = [
  "delivered",
  "accepted",
  "in_progress",
  "retrying",
  "customer_not_found",
  "no_subscription",
  "subscription_invalid",
  "unsubscribed",
  "address_invalid",
  "token_wrong_project",
  "credential_problem",
  "provider_unavailable",
  "payload_rejected",
  "unknown_outcome",
  "bounced",
  "other",
] as const;
export type DeliveryIssue = (typeof DELIVERY_ISSUES)[number];

/** Issue kinds that mean the recipient did not get the message. */
export const PROBLEM_ISSUES: ReadonlySet<DeliveryIssue> = new Set<DeliveryIssue>([
  "customer_not_found",
  "no_subscription",
  "subscription_invalid",
  "unsubscribed",
  "address_invalid",
  "token_wrong_project",
  "credential_problem",
  "provider_unavailable",
  "payload_rejected",
  "unknown_outcome",
  "bounced",
  "other",
]);

/** Error codes per issue kind. Codes not listed here are reported as `other`. */
export const ISSUE_ERROR_CODES: Partial<Record<DeliveryIssue, readonly string[]>> = {
  customer_not_found: ["customer_not_found"],
  no_subscription: ["no_subscription"],
  subscription_invalid: ["subscription_invalid"],
  unsubscribed: ["unsubscribed"],
  address_invalid: [
    "fcm_unregistered",
    "fcm_invalid_registration",
    "smtp_invalid_envelope",
    "apns_bad_device_token",
    "apns_unregistered",
  ],
  token_wrong_project: ["fcm_sender_id_mismatch"],
  credential_problem: [
    "fcm_auth_error",
    "fcm_third_party_auth_error",
    "smtp_auth_error",
    "invalid_credential_config",
    "credential_not_allowed",
    "credential_channel_mismatch",
    "no_credential",
    "adapter_not_found",
  ],
  provider_unavailable: [
    "provider_unavailable",
    "provider_timeout",
    "network_error",
    "adapter_exception",
    "fcm_unavailable",
    "fcm_internal",
    "fcm_quota_exceeded",
    "fcm_error",
    "smtp_error",
    "stuck_requeued",
  ],
  payload_rejected: ["fcm_invalid_argument", "payload_too_large"],
  unknown_outcome: ["unknown_outcome"],
};

const CODE_TO_ISSUE = new Map<string, DeliveryIssue>();
for (const [issue, codes] of Object.entries(ISSUE_ERROR_CODES)) {
  for (const code of codes ?? []) CODE_TO_ISSUE.set(code, issue as DeliveryIssue);
}

/** Codes built from provider responses at runtime, e.g. `smtp_550` or `smtp_econnection`. */
function issueFromPattern(code: string): DeliveryIssue | null {
  const smtpReply = /^smtp_([0-9]{3})$/.exec(code);
  if (smtpReply) {
    const reply = Number(smtpReply[1]);
    if (reply >= 400 && reply < 500) return "provider_unavailable";
    // 550 mailbox unavailable, 551 user not local, 553 mailbox name not allowed.
    if (reply === 550 || reply === 551 || reply === 553) return "address_invalid";
    return "other";
  }
  // Connection-level SMTP errors from nodemailer, e.g. smtp_econnection or smtp_etimedout.
  if (/^smtp_e[a-z]+$/.test(code)) return "provider_unavailable";
  return null;
}

/**
 * What a manual retry does with a delivery, by issue kind (D-51).
 * - `resend`: send the same content again, with the credentials and customer addresses as they are now.
 * - `recheck`: the recipient had no usable address; send only if the customer has one now.
 * - `confirm`: the message may already have gone out and been charged, so only on explicit request.
 * - `never`: a retry cannot help (opt-out, rejected content, bounce, or an ad-hoc address that is dead).
 */
export const RETRY_ACTIONS = ["resend", "recheck", "confirm", "never"] as const;
export type RetryAction = (typeof RETRY_ACTIONS)[number];

export const ISSUE_RETRY_ACTIONS: Record<DeliveryIssue, RetryAction> = {
  delivered: "never",
  accepted: "never",
  in_progress: "never",
  retrying: "never",
  customer_not_found: "recheck",
  no_subscription: "recheck",
  subscription_invalid: "recheck",
  unsubscribed: "never",
  // A customer may have registered a new address since; an ad-hoc address stays dead (see retryAction).
  address_invalid: "recheck",
  // Usually a wrong credential behind the alias, which an admin can fix.
  token_wrong_project: "resend",
  credential_problem: "resend",
  provider_unavailable: "resend",
  payload_rejected: "never",
  unknown_outcome: "confirm",
  bounced: "never",
  other: "resend",
};

/** Retry action for one delivery. Deliveries without a customer have no other address to fall back to. */
export function retryAction(issue: DeliveryIssue, hasCustomer: boolean): RetryAction {
  const action = ISSUE_RETRY_ACTIONS[issue];
  return action === "recheck" && !hasCustomer ? "never" : action;
}

/** Classify a delivery from its status and error code. */
export function deliveryIssue(status: DeliveryStatus, errorCode: string | null | undefined): DeliveryIssue {
  switch (status) {
    case "delivered":
      return "delivered";
    case "sent":
      return "accepted";
    case "bounced":
      return "bounced";
    case "pending":
    case "sending":
      // A pending delivery with an error code is waiting for its next retry.
      return errorCode ? "retrying" : "in_progress";
    default:
      if (!errorCode) return "other";
      return CODE_TO_ISSUE.get(errorCode) ?? issueFromPattern(errorCode) ?? "other";
  }
}
